import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { AgeCategory, YouthChildrenSubCategory } from '../../../types/camper';
import './AttendanceStats.css';

interface AttendanceCounts {
  adults: number;
  adultStudents: number;
  youthChildren: {
    total: number;
    nursery: number;
    preSchool: number;
    gradeP5: number;
    grade68: number;
    grade912: number;
    unspecified: number;
  };
}

const ACCOMMODATION_TYPE_LABELS: Record<string, string> = {
  Cottage: 'Cottage',
  Apartment: 'Apartment',
  dormA: 'Dorm A',
  dormB: 'Dorm B',
  Fireside: 'Fireside',
  LowerCourt: 'Lower Court',
  UpperCourt: 'Upper Court',
  privateCottage: 'Private Cottage',
};

interface AccommodationInfo {
  type: string;
  label: string;
  occupied: number;
  total: number;
}

const isMaintenanceUnit = (unit: { status?: string }) =>
  String(unit.status || '').toLowerCase() === 'maintenance';

const countUsableUnits = (units: { status?: string }[]) =>
  units.filter(unit => !isMaintenanceUnit(unit)).length;

const AttendanceStats: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<AttendanceCounts>({
    adults: 0,
    adultStudents: 0,
    youthChildren: {
      total: 0,
      nursery: 0,
      preSchool: 0,
      gradeP5: 0,
      grade68: 0,
      grade912: 0,
      unspecified: 0
    }
  });
  const [accommodationInfo, setAccommodationInfo] = useState<AccommodationInfo[]>([]);

  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        setLoading(true);
        setError(null);

        const registrationsRef = collection(db, 'Registrations');
        const snapshot = await getDocs(registrationsRef);
        
        const newCounts: AttendanceCounts = {
          adults: 0,
          adultStudents: 0,
          youthChildren: {
            total: 0,
            nursery: 0,
            preSchool: 0,
            gradeP5: 0,
            grade68: 0,
            grade912: 0,
            unspecified: 0
          }
        };

        // For debugging
        let totalRegistrations = 0;
        let pendingCount = 0;
        let processedCount = 0;
        const statusCounts: { [key: string]: number } = {};

        snapshot.forEach(doc => {
          const data = doc.data();
          totalRegistrations++;
          
          // Track status counts
          const status = data.status?.toLowerCase() || 'undefined';
          statusCounts[status] = (statusCounts[status] || 0) + 1;
          
          // Skip pending registrations (not yet assigned)
          if (status === 'pending') {
            pendingCount++;
            return;
          }
          
          if (data.attendingCampers && Array.isArray(data.attendingCampers)) {
            processedCount++;
            console.log(`Processing registration ${doc.id}:`, {
              status: data.status,
              numCampers: data.attendingCampers.length,
              campers: data.attendingCampers.map((c: any) => ({ 
                name: `${c.firstName} ${c.lastName}`, 
                age: c.age, 
                category: c.category 
              }))
            });
            
            data.attendingCampers.forEach((camper: any) => {
              const ageCategory = camper.age || camper.category;
              console.log(`  Camper: ${camper.firstName} ${camper.lastName}, age/category:`, ageCategory);
              
              switch (ageCategory) {
                case AgeCategory.Adult:
                  newCounts.adults++;
                  break;
                case AgeCategory.AdultStudent:
                  newCounts.adultStudents++;
                  break;
                case AgeCategory.YouthChildren:
                  newCounts.youthChildren.total++;
                  switch (camper.YouthChildrenCategory || camper.youthChildrenCategory || camper.youthChildrenSubCategory) {
                    case YouthChildrenSubCategory.Nursery:
                      newCounts.youthChildren.nursery++;
                      break;
                    case YouthChildrenSubCategory.PreSchool:
                      newCounts.youthChildren.preSchool++;
                      break;
                    case YouthChildrenSubCategory.GradeP5:
                      newCounts.youthChildren.gradeP5++;
                      break;
                    case YouthChildrenSubCategory.Grade68:
                      newCounts.youthChildren.grade68++;
                      break;
                    case YouthChildrenSubCategory.Grade912:
                      newCounts.youthChildren.grade912++;
                      break;
                    default:
                      newCounts.youthChildren.unspecified++;
                  }
                  break;
                default:
                  console.log(`  Unknown age category: ${ageCategory}`);
              }
            });
          } else {
            console.log(`Registration ${doc.id} has no attendingCampers array`);
          }
        });

        console.log('Attendance Stats Summary:', {
          totalRegistrations,
          pendingCount,
          processedCount,
          statusCounts,
          counts: newCounts
        });

        setCounts(newCounts);
      } catch (err) {
        console.error('Error fetching attendance data:', err);
        setError('Failed to load attendance data');
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, []);

  useEffect(() => {
    const fetchAccommodationInfo = async () => {
      try {
        const inventoryRef = doc(db, 'inventory', 'accommodations');
        const docSnap = await getDoc(inventoryRef);
        const data = docSnap.data();
        if (!data) return;
        
        // Create combined accommodation info
        const info: AccommodationInfo[] = [];
        
        // Combine regular cottages and private cottages
        const regularCottages = Array.isArray(data.Cottage) ? data.Cottage : [];
        const privateCottages = Array.isArray(data.privateCottage) ? data.privateCottage : [];
        const allCottages = [...regularCottages, ...privateCottages];
        
        const cottageOccupied = allCottages.filter((unit: any) => {
          const status = String(unit.status || '').toLowerCase();
          return status === 'occupied' || status === 'held';
        }).length;
        
        info.push({
          type: 'Cottage',
          label: 'Cottages (including staff)',
          occupied: cottageOccupied,
          total: countUsableUnits(allCottages),
        });
        
        // Add other accommodation types (excluding privateCottage since it's now combined)
        const otherTypes = ['Apartment', 'dormA', 'dormB', 'Fireside', 'LowerCourt', 'UpperCourt'];
        otherTypes.forEach(type => {
          const arr = Array.isArray(data[type]) ? data[type] : [];
          const occupied = arr.filter((unit: any) => {
            const status = String(unit.status || '').toLowerCase();
            return status === 'occupied' || status === 'held';
          }).length;
          info.push({
            type,
            label: ACCOMMODATION_TYPE_LABELS[type],
            occupied,
            total: countUsableUnits(arr),
          });
        });
        
        setAccommodationInfo(info);
      } catch (err) {
        // Optionally handle error
      }
    };
    fetchAccommodationInfo();
  }, []);

  const totalRegistered = counts.adults + counts.adultStudents + counts.youthChildren.total;

  if (loading) {
    return <div className="attendance-stats loading">Loading attendance data...</div>;
  }

  if (error) {
    return <div className="attendance-stats error">{error}</div>;
  }

  return (
    <div className="attendance-stats">
      <h2>Attendance Stats</h2>
      
      <div className="stats-section">
        <div className="section-header">
          <h3>Registration Information</h3>
          <span className="total-registered">Total Registered: {totalRegistered}</span>
        </div>
        
        <table className="stats-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Registered</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Adults</td>
              <td>{counts.adults}</td>
            </tr>
            <tr>
              <td>Adult Students</td>
              <td>{counts.adultStudents}</td>
            </tr>
            <tr className="youth-children-header">
              <td>Youth/Children</td>
              <td>{counts.youthChildren.total}</td>
            </tr>
            <tr className="subcategory">
              <td>Nursery</td>
              <td>{counts.youthChildren.nursery}</td>
            </tr>
            <tr className="subcategory">
              <td>PreSchool</td>
              <td>{counts.youthChildren.preSchool}</td>
            </tr>
            <tr className="subcategory">
              <td>Grade P-5</td>
              <td>{counts.youthChildren.gradeP5}</td>
            </tr>
            <tr className="subcategory">
              <td>Grade 6-8</td>
              <td>{counts.youthChildren.grade68}</td>
            </tr>
            <tr className="subcategory">
              <td>Grade 9-12</td>
              <td>{counts.youthChildren.grade912}</td>
            </tr>
            <tr className="subcategory">
              <td>Unspecified</td>
              <td>{counts.youthChildren.unspecified}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="stats-section">
        <h3>Accommodation Information</h3>
        <table className="stats-table">
          <thead>
            <tr>
              <th>Accommodation Type</th>
              <th>Occupied / Total Units</th>
            </tr>
          </thead>
          <tbody>
            {accommodationInfo.map(info => (
              <tr key={info.type}>
                <td>{info.label}</td>
                <td>{info.occupied} / {info.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceStats; 